import {of, merge, combineLatest, empty, defer, EMPTY} from 'rxjs';
import {mergeMap, map, filter, debounceTime, catchError, delay, flatMap, take, tap} from 'rxjs/operators';
import {ofType, type Epic} from 'redux-observable';
import {createAction} from 'redux-actions';
import {change, reset, stopSubmit, startSubmit, isDirty, getFormNames} from 'redux-form';
import i18next from 'i18next';
import moment from 'moment-timezone';
import {List} from 'immutable';

import * as actionTypes from 'web-app/react/constants/action-types';
import * as NewPostConstants from 'web-app/react/feed/post/new-post/constants';
import * as NewPostObservables from 'web-app/react/feed/post/new-post/epics';
import * as DraftObservables from 'web-app/react/entities/drafts/epics';
import * as DraftActions from 'web-app/react/entities/drafts/actions';
import RecipientActionTypes from 'web-app/react/entities/recipients/action-types';
import * as FileActions from 'web-app/react/components/file-upload/actions';
import {clearFiles} from 'web-app/react/components/file-upload/actions';
import * as GlobalEventActions from 'web-app/react/global-event/actions';
import * as DraftManagerModalActions from 'web-app/react/modals-container/draft-manager-modal/actions';
import * as FeedItemObservables from 'web-app/react/entities/feed/items/epics';
import {ObservationVariant} from 'web-app/react/entities/observations/constants';
import * as AssessmentHelpers from 'web-app/react/feed/post/new-post/new-assessment/helpers';
import * as SentryService from 'web-app/services/sentry';
import {showDialogObservable} from 'web-app/react/dialog';
import * as DraftManagerSelectors from 'web-app/react/modals-container/draft-manager-modal/selectors';
import {getValueForRichTextBody} from 'web-app/react/entities/feed/items/helpers';
import {getVariantAmbiguityText} from 'web-app/react/entities/observations/helpers';
import type {RootState} from 'web-app/react/redux/root-state';
import {combineEpics} from 'web-app/util/redux-observable';
import PostApi from 'web-app/api/post-api';

import * as Actions from './actions';
import * as Selectors from './selectors';

/** SEND DRAFT **/
const sendDraft: Epic<any, any, RootState, any> = action$ =>
    action$.pipe(
        ofType(Actions.send.type),
        mergeMap(({payload}) => of(startSubmit(payload.formIdentifier) as any)),
    );

/** CREATE OR UPDATE DRAFT (upsert) **/
const isNewPostForm = ({payload}: any) => NewPostConstants.knownFormValues.includes(payload.formName);

const createOrUpdate: Epic<any, any, RootState, any> = (action$, state$) =>
    merge(
        action$.pipe(
            ofType(Actions.newPostFormDidChange.type),
            filter(({payload}) => isDirty(payload.formName)(state$.value)),
        ),
        action$.pipe(
            ofType(actionTypes.FILE_UPLOAD_FILE_PERSIST_SUCCESS),
            filter(action => isNewPostForm(action) && !DraftManagerSelectors.draftManagerIsEditting(state$.value)),
        ),
        action$.pipe(
            ofType(actionTypes.FILE_UPLOAD_REMOVE_FILE),
            filter(action => isNewPostForm(action) && !DraftManagerSelectors.draftManagerIsEditting(state$.value)),
        ),
    ).pipe(map(({payload}) => Actions.createOrUpdate.action(payload.formName)));

/** LISTEN TO DRAFT CHANGES **/
const draftContainsInvalidRecipients = (draft: any): boolean => {
    if (draft.isObservation || !draft.recipients) {
        return false;
    }
    const recipients = draft.recipients.split(',');
    return recipients.some(rec => !rec.includes('Famly'));
};

const isValidDraft = (draft: any): boolean => {
    if (
        !draft.isObservation ||
        (draft.observationData.variant !== ObservationVariant.TWO_YEAR_PROGRESS &&
            draft.observationData.variant !== ObservationVariant.ASSESSMENT) ||
        !draft.observationData.areaRefinements
    ) {
        return true;
    }
    const areaRefinements = List.isList(draft.observationData.areaRefinements)
        ? draft.observationData.areaRefinements.toArray()
        : draft.observationData.areaRefinements;
    return areaRefinements.every(
        (areaRefinement: any) => areaRefinement.areaId && (areaRefinement.refinement || areaRefinement.note),
    );
};

const listenToDraftChanges: Epic<any, any, RootState, any> = (action$, state$) => {
    const createOrUpdateObservable = merge(
        action$.pipe(ofType<any>(Actions.createOrUpdate.type)),
        action$.pipe(
            ofType(Actions.clearDraft.type, Actions.selectDraft.type),
            map(() => null),
        ),
    ).pipe(
        debounceTime(5000),
        filter(action => (action ? getFormNames()(state$.value).includes((action.payload as any).formName) : false)),
        map(action => (action ? Selectors.getCurrentDraft(state$.value, {formName: action.payload.formName}) : null)),
        filter(draft => {
            return Boolean(draft);
        }),
        filter(isValidDraft),
        tap(draft => {
            if (draftContainsInvalidRecipients(draft)) {
                SentryService.captureMessage('Invalid recipients detected', {
                    fingerprint: ['1516284299'],
                    extra: {
                        isObservation: draft.isObservation,
                        recipients: draft.recipients,
                    },
                });
            }
        }),
    );

    const isCreate = (obj: any): boolean => !obj.draftId;

    const updateObservable = createOrUpdateObservable.pipe(
        filter(obj => !isCreate(obj)),
        map(obj =>
            Actions.update.action({
                draftId: obj.draftId,
                recipients: obj.recipients,
                from: obj.from,
                body: obj.body,
                richTextBody: obj.richTextBody,
                files: obj.files,
                observationData: obj.observationData,
                disableFeedback: obj.disableFeedback,
                activities: obj.activities,
                famlyLiveMeetingId: obj.famlyLiveMeetingId,
                pollId: obj.pollId,
            }),
        ),
    );
    const createObservable = createOrUpdateObservable.pipe(
        filter(obj => isCreate(obj)),
        map(obj =>
            DraftActions.create.action({
                recipients: obj.recipients,
                from: obj.from,
                body: obj.body,
                richTextBody: obj.richTextBody,
                files: obj.files,
                observationData: obj.observationData,
                disableFeedback: obj.disableFeedback,
                activities: obj.activities,
                famlyLiveMeetingId: obj.famlyLiveMeetingId,
                pollId: obj.pollId,
            }),
        ),
    );
    return merge(updateObservable, createObservable);
};

/** HANDLE OBSERVATION DATE WARNING **/
const shouldShowObservationDateWarning = (status: any, observedAt: any): boolean =>
    status === 'SENT' && observedAt && moment().diff(moment(observedAt, 'YYYY-MM-DD'), 'months') >= 1;

export const handleObservationDateWarningObservable = (variant: any, status: any, observedAt: any) => {
    if (shouldShowObservationDateWarning(status, observedAt)) {
        return showDialogObservable({
            description: i18next.t('childDevelopment.observedAtWarningDesc', {
                type: getVariantAmbiguityText(variant),
                timeAgo: moment(observedAt).fromNow(),
            }),
            title: i18next.t('childDevelopment.observedAtWarningTitle'),
            okText: i18next.t('confirm'),
        });
    }
    return of('OK');
};

/** LISTEN TO DRAFT OBSERVABLES **/
const listenToDraftObservables: Epic<any, any, RootState, any> = (action$, state$) => {
    const createSuccessObservable = action$.pipe(
        ofType(DraftActions.createSuccess.type),
        map(({payload}) => payload.draftId),
    );
    const selectObservable = action$.pipe(
        ofType(Actions.selectDraftSuccess.type),
        map(({payload}) => payload.draftId),
    );
    const flushObservable = action$.pipe(
        ofType(
            DraftActions.sendSuccess.type,
            Actions.clearDraftSuccess.type,
            Actions.showObservationForm.type,
            DraftManagerModalActions.show.type,
            DraftManagerModalActions.hide.type,
        ),
        map(() => null),
    );
    const didReceiveDraftId = merge(createSuccessObservable, selectObservable, flushObservable);

    const updateObservable = merge(
        action$.pipe(ofType<any>(Actions.update.type, Actions.send.type)),
        selectObservable.pipe(map(() => null)),
        action$.pipe(
            ofType(Actions.selectDraft.type),
            map(() => null),
        ),
        flushObservable,
    );

    return combineLatest([updateObservable, didReceiveDraftId]).pipe(
        mergeMap(([updateAction, draftId]) => {
            if (!draftId || !updateAction) {
                return EMPTY;
            }
            const {
                recipients,
                from,
                body,
                richTextBody,
                files,
                formIdentifier,
                send,
                observationData,
                disableFeedback,
                activities,
                famlyLiveMeetingId,
                facebookPageIds,
                rssInstitutionSetIds,
                overridePostBlock,
                sendAsSms,
                pollId,
            } = updateAction.payload;

            if (send) {
                const sendDraftObservable = defer(() =>
                    DraftObservables.sendDraftObservable(
                        draftId,
                        recipients,
                        from,
                        body,
                        richTextBody,
                        files,
                        observationData,
                        disableFeedback,
                        activities,
                        famlyLiveMeetingId,
                        facebookPageIds,
                        rssInstitutionSetIds,
                        overridePostBlock,
                        sendAsSms,
                        pollId,
                    ).pipe(
                        flatMap(sendAction => {
                            if (sendAction && (sendAction.type as any) === DraftActions.sendAborted.type) {
                                return [sendAction, stopSubmit(formIdentifier), Actions.sendAborted.action(draftId)];
                            }
                            return [
                                sendAction,
                                GlobalEventActions.updateSuccess.action(getSendLabel(observationData)),
                                stopSubmit(formIdentifier),
                                reset(formIdentifier),
                                clearFiles(formIdentifier),
                                DraftManagerModalActions.hide.action(),
                                Actions.sendSuccess.action({draftId, famlyLiveMeetingId}),
                            ];
                        }),
                        catchError(e =>
                            merge(
                                of(GlobalEventActions.updateError.action(i18next.t('feed.notSent'))),
                                of(stopSubmit(formIdentifier)),
                                of(Actions.sendFailed.action(e)),
                            ),
                        ),
                    ),
                );
                if (!observationData) {
                    return sendDraftObservable;
                }
                return handleObservationDateWarningObservable(
                    observationData.variant,
                    observationData.status,
                    observationData.observedAt,
                ).pipe(
                    flatMap(selection => {
                        if (selection === 'OK') {
                            return sendDraftObservable;
                        }
                        return [stopSubmit(formIdentifier), Actions.sendAborted.action(draftId)];
                    }),
                );
            } else if (state$.value.feed.draftManager.posting) {
                return empty();
            }
            return DraftObservables.updateDraftObservable(
                draftId,
                recipients,
                from,
                body,
                richTextBody,
                files,
                observationData,
                disableFeedback,
                activities,
                famlyLiveMeetingId,
                pollId,
            ).pipe(catchError(e => of(Actions.createOrUpdateFailed.action(e))));
        }),
    );
};

const getSendLabel = (observationData: any): string => {
    if (observationData && observationData.status === 'SENT') {
        return i18next.t('feed.obsSent');
    } else if (observationData) {
        return i18next.t('feed.obsSentToReview');
    } else {
        return i18next.t('feed.postSent');
    }
};

/** SELECT DRAFT OBSERVABLE **/
const selectDraftObservable = (form: string, draft: any, canUseRichTextInPosts: boolean) => {
    const {
        isObservation,
        body,
        nextStep,
        from,
        observedAt,
        createdAt,
        variant,
        activities,
        famlyLiveMeetingId,
        pollId,
    } = draft;
    const richTextBody = getValueForRichTextBody(draft);
    const selectedRecipients = draft.recipients.map((rec: any) => rec.id).join(',');

    const recipientsFetch = isObservation
        ? NewPostObservables.searchChildrenObservable(selectedRecipients, [])
        : PostApi.getPossibleRecipients({from}).pipe(
              map(result =>
                  // Using createAction here for a one‑off recipients fetch success
                  // (adjust the typing if needed)
                  createAction(RecipientActionTypes.RECIPIENTS_FETCH_SUCCESS)({
                      type: 'post',
                      recipients: result,
                  }),
              ),
          );
    return recipientsFetch.pipe(
        mergeMap(action =>
            merge(
                of(action),
                merge(
                    of(
                        Actions.selectDraftSuccess.action({
                            draftId: draft.postId,
                            showObservationForm: isObservation,
                            variant: draft.variant,
                        }),
                    ),
                    of(FileActions.loadFiles(form, draft.images.concat(draft.files))),
                    canUseRichTextInPosts && !isObservation
                        ? of(change(form, 'richTextBody', richTextBody))
                        : of(change(form, 'body', body)),
                    of(change(form, 'nextStep', nextStep)),
                    of(change(form, 'observedAt', observedAt ? observedAt : moment(createdAt).format('YYYY-MM-DD'))),
                    isObservation ? empty() : of(change(form, 'from', from)),
                    of(change(form, 'selectedRecipients', '')),
                    of(change(form, 'selectedRecipients', selectedRecipients ? selectedRecipients : null)),
                    of(change(form, 'disableFeedback', draft.disableFeedback)),
                    of(
                        change(
                            form,
                            'statements',
                            draft.statements
                                .map((stat: any) => ({
                                    statementId: stat.statement.id,
                                    refinement: stat.refinement,
                                }))
                                .toArray(),
                        ),
                    ),
                    of(
                        change(
                            form,
                            'areaRefinements',
                            AssessmentHelpers.prepareAreaRefinementForForm(variant, draft.areaRefinements.toArray()),
                        ),
                    ),
                    of(change(form, 'activities', activities)),
                    of(change(form, 'famlyLiveMeetingId', famlyLiveMeetingId)),
                    of(change(form, 'pollId', pollId)),
                ).pipe(delay(100)),
            ),
        ),
        catchError(e => of(Actions.selectDraftFailed.action(e))),
    );
};

/** SELECT DRAFT **/
const selectDraft: Epic<any, any, RootState, any> = (action$, state$) =>
    action$.pipe(
        ofType(Actions.selectDraft.type),
        mergeMap(({payload}) => {
            const {form, draft, draftIsDirty, canUseRichTextInPosts} = payload;
            if (!form) {
                return of(Actions.selectDraftFailed.action());
            }
            if (draftIsDirty) {
                const currentDraft = Selectors.getCurrentDraft(state$.value, {formName: form});
                const {
                    draftId,
                    from,
                    body,
                    richTextBody,
                    files,
                    recipients,
                    observationData,
                    disableFeedback,
                    activities,
                    famlyLiveMeetingId,
                    pollId,
                } = currentDraft || {};

                let draftObservable;
                if (draftId) {
                    draftObservable = DraftObservables.updateDraftObservable(
                        draftId,
                        recipients,
                        from,
                        body,
                        richTextBody,
                        files,
                        observationData,
                        disableFeedback,
                        activities,
                        famlyLiveMeetingId,
                        pollId,
                    ).pipe(mergeMap(() => selectDraftObservable(form, draft, canUseRichTextInPosts)));
                } else {
                    draftObservable = selectDraftObservable(form, draft, canUseRichTextInPosts);
                }
                return draftObservable.pipe(catchError(e => of(Actions.clearDraftFailed.action(e))));
            }
            return selectDraftObservable(form, draft, canUseRichTextInPosts);
        }),
    );

/** CLEAR FORM OBSERVABLE **/
const clearFormObservable = (form: string, showObservationForm = false) =>
    merge(of(reset(form)), of(FileActions.clearFiles(form)), of(Actions.clearDraftSuccess.action(showObservationForm)));

/** CLEAR DRAFT **/
const clearDraft: Epic<any, any, RootState, any> = (action$, state$) =>
    action$.pipe(
        ofType(Actions.clearDraft.type),
        mergeMap(({payload}) => {
            const {form, draftIsDirty} = payload;
            if (!form) {
                return of(Actions.clearDraftFailed.action());
            }
            const currentDraft = Selectors.getCurrentDraft(state$.value, {formName: form});
            if (draftIsDirty) {
                const {
                    draftId,
                    from,
                    body,
                    richTextBody,
                    nextStep,
                    files,
                    recipients,
                    observationData,
                    disableFeedback,
                    activities,
                    famlyLiveMeetingId,
                    pollId,
                } = currentDraft || {};

                if (
                    draftId ||
                    body ||
                    nextStep ||
                    (files && files.size > 0) ||
                    (observationData && observationData.statements && observationData.statements.length > 0)
                ) {
                    const draftObservable = draftId
                        ? DraftObservables.updateDraftObservable(
                              draftId,
                              recipients,
                              from,
                              body,
                              richTextBody,
                              files,
                              observationData,
                              disableFeedback,
                              activities,
                              famlyLiveMeetingId,
                              pollId,
                          )
                        : DraftObservables.createDraftObservable(
                              recipients,
                              from,
                              body,
                              richTextBody,
                              files,
                              observationData,
                              disableFeedback,
                              activities,
                              famlyLiveMeetingId,
                              pollId,
                          );
                    return draftObservable.pipe(
                        mergeMap(success => merge(of(success), clearFormObservable(form, payload.showObservationForm))),
                        catchError(e => of(Actions.clearDraftFailed.action(e))),
                    );
                }
            }
            return clearFormObservable(form, payload.showObservationForm);
        }),
    );

/** CLEAR DRAFT IF CURRENT **/
const clearDraftIfCurrent: Epic<any, any, RootState, any> = action$ =>
    action$.pipe(
        ofType(DraftActions.removeSuccess.type),
        filter(({payload}) => {
            const {draftId, extraData} = payload;
            return Boolean(extraData) && extraData.form && draftId === extraData.current;
        }),
        mergeMap(({payload}) => {
            const {extraData} = payload;
            return merge(of(reset(extraData.form)), of(FileActions.clearFiles(extraData.form)));
        }),
    );

/** UPDATE ITEM **/
const updateItem: Epic<any, any, RootState, any> = action$ =>
    action$.pipe(
        ofType(Actions.updatePost.type),
        mergeMap(({payload}) => {
            const {
                postId,
                recipients,
                from,
                body,
                richTextBody,
                files,
                observationData,
                formIdentifier,
                disableFeedback,
                reNotify,
                activities,
                famlyLiveMeetingId,
            } = payload;
            return merge(
                of(startSubmit(formIdentifier)),
                FeedItemObservables.updateItemObservable(
                    postId,
                    recipients,
                    from,
                    body,
                    richTextBody,
                    files,
                    observationData,
                    disableFeedback,
                    reNotify,
                    activities,
                    famlyLiveMeetingId,
                ).pipe(
                    flatMap(successAction => [
                        successAction,
                        GlobalEventActions.updateSuccess.action(i18next.t('feed.postUpdated')),
                        stopSubmit(formIdentifier),
                        reset(formIdentifier),
                        clearFiles(formIdentifier),
                        Actions.updatePostSuccess.action(),
                    ]),
                    catchError(e => merge(of(stopSubmit(formIdentifier)), of(Actions.updatePostFailed.action(e)))),
                ),
            );
        }),
    );

/** EDIT OBSERVATION DRAFT **/
const editObservationDraft: Epic<any, any, RootState, any> = action$ =>
    action$.pipe(
        ofType(DraftManagerModalActions.showObservationDraft.type),
        mergeMap(observationNotification =>
            combineLatest([
                of(observationNotification),
                action$.pipe(ofType(DraftActions.fetchAllSuccess.type), take(1)),
            ]),
        ),
        flatMap(([observationNotification, fetchNotification]): any => {
            const {observationId} = observationNotification.payload;
            const {drafts} = fetchNotification.payload;
            const draftItem = drafts.find((draft: any) => draft.postId === observationId);
            if (!draftItem) {
                return [
                    GlobalEventActions.updateError.action(i18next.t('feed.observationNotFound')),
                    DraftManagerModalActions.hide.action(),
                ];
            }
            return [
                Actions.selectDraft.action({
                    form: NewPostConstants.OBSERVATION_FORM_NAME,
                    draft: draftItem,
                    draftIsDirty: false,
                }),
            ];
        }),
    );

export default combineEpics(
    editObservationDraft,
    createOrUpdate,
    clearDraft,
    listenToDraftObservables,
    sendDraft,
    listenToDraftChanges,
    clearDraftIfCurrent,
    updateItem,
    selectDraft,
);
